import React, { useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';

import { Translator } from 'ggApp/modules/translator';
import { getKeyForVoucherError } from 'ggApp/modules/form/components/voucherFields';
import { registerVoucherValidationV2 } from 'ggApp/modules/gaAnalytics/actions';

import { formatPriceByLanguage } from 'ggApp/utils/money';
import { CURRENCIES_ASSOCIATION } from 'ggApp/modules/store/constants';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';

import {
    Wrap,
    Title,
    InputWrap,
    InputField,
    SubmitButton,
    VoucherWrap,
    ResetButton,
    ResetIcon,
    VoucherCode,
    VoucherAmount,
    ButtonWrap,
} from './styles';

const VoucherCard = ({ className, theme, translations, unavailableProducts }) => {
    const { user, activeStore, cart, orderNumber, send, current } = useContext(OrderContext);
    const hasVoucher = Boolean(cart.voucher && cart.voucher.name);

    const [error, setError] = useState();
    const [voucherFieldValue, setVoucherFieldValue] = useState('');
    const [inputBoxDisplayed, setInputBoxDisplayed] = useState();
    const [voucherCodeDisplayed, setVoucherCodeDisplayed] = useState();

    useEffect(() => {
        setInputBoxDisplayed(!hasVoucher);
        setVoucherCodeDisplayed(hasVoucher);
    }, [hasVoucher]);

    useEffect(() => {
        if (current.context.voucherError) {
            const errorTk = getKeyForVoucherError(current.context.voucherError);
            setError(errorTk);

            registerVoucherValidationV2({
                eventLabel: 'not_valid',
                orderNumber,
                userID: user.id,
                voucher: voucherFieldValue,
                error: errorTk,
            });
        }
    }, [setError, current, cart, voucherFieldValue, orderNumber, user.id]);

    const handleChange = useCallback(
        (e) => {
            if (!!current.context.voucherError) {
                send('SET_VOUCHER_ERROR');
            }
            setError(null);
            setVoucherFieldValue(e.target.value);
        },
        [send, current.context.voucherError],
    );

    const handleSubmit = useCallback(() => {
        const payalod = {
            voucher: voucherFieldValue,
            translationOptions: {
                lang: activeStore.activeLanguage,
                keys: translations,
            },
        };
        const isUnavailableFlow = Array.isArray(unavailableProducts) && unavailableProducts.length;
        const event = isUnavailableFlow
            ? 'SUBMIT_VOUCHER_WITH_AVAILABLEP_RODUCTS'
            : 'SUBMIT_VOUCHER';
        if (isUnavailableFlow) {
            payalod.unavailableItems = unavailableProducts;
        }

        send(event, payalod);
    }, [voucherFieldValue, activeStore.activeLanguage, translations, unavailableProducts, send]);

    const handleReset = useCallback(() => {
        setVoucherFieldValue(null);
        setVoucherCodeDisplayed(false);
        setInputBoxDisplayed(true);

        send('RESET_VOUCHER');
    }, [send]);

    return (
        <Wrap className={className}>
            <Title>
                <Translator tk="NEW_CHECKOUT_VOUCHER_CARD_TITLE" />
            </Title>
            {!cart.voucher || !cart.voucher.name ? (
                <InputWrap data-testid="voucher-input-wrap" mounted={inputBoxDisplayed}>
                    <InputField
                        className="voucherInput"
                        data-testid="voucher-input-field"
                        error={error && <Translator tk={error} />}
                        onChange={handleChange}
                        value={voucherFieldValue}
                    />
                    <ButtonWrap>
                        <SubmitButton
                            isDisabled={!voucherFieldValue || error}
                            outlined
                            small
                            data-testid="submit-voucher-button"
                            onClick={handleSubmit}
                        >
                            <Translator tk="NEW_CHECKOUT_VOUCHER_CARD_SUBMIT_TEXT" />
                        </SubmitButton>
                    </ButtonWrap>
                </InputWrap>
            ) : (
                <VoucherWrap mounted={voucherCodeDisplayed}>
                    <ResetButton onClick={handleReset}>
                        <ResetIcon fill={theme.colors.secondary} />
                    </ResetButton>
                    <VoucherCode>{cart.voucher.name}</VoucherCode>
                    <VoucherAmount>
                        {formatPriceByLanguage(
                            cart.voucher.discount_cents,
                            CURRENCIES_ASSOCIATION[activeStore.default_currency],
                            activeStore.activeLanguage,
                        )}
                    </VoucherAmount>
                </VoucherWrap>
            )}
        </Wrap>
    );
};

VoucherCard.propTypes = {
    className: PropTypes.string,
    theme: PropTypes.shape({}).isRequired,
    translations: PropTypes.shape({}).isRequired,
    unavailableProducts: PropTypes.arrayOf({}),
};

VoucherCard.defaultProps = {
    className: undefined,
    unavailableProducts: undefined,
};

export default withTheme(VoucherCard);
